import ScrollBuffer from 'components/AI/ThanxAIAutoScrollContainer/ScrollBuffer'
import { useCallback, useEffect, useRef } from 'react'

// As complexity increases here, it's worth considering refactoring to a state-based approach
// instead of direct DOM manipulation and complex ref communication between AutoScrollContainer
// and ScrollBuffer components.

const SCROLL_OFFSET_FROM_TOP = 95
const CONTAINER_PADDING_TOP = 60

export interface ThanxAIAutoScrollContainerProps {
  children: React.ReactNode
  className?: string
  enabled?: boolean
  scrollTrigger?: any
}

const ThanxAIAutoScrollContainer = ({
  children,
  className = '',
  enabled = true,
  scrollTrigger,
}: ThanxAIAutoScrollContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollBufferRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<MutationObserver | null>(null)
  const scrollToNewMessageRef = useRef<(() => void) | null>(null)

  const updateScrollBuffer = useCallback(() => {
    if (
      containerRef.current &&
      (containerRef.current as any)._updateScrollBuffer
    ) {
      ;(containerRef.current as any)._updateScrollBuffer()
    }
  }, [])

  const handleNewMessage = useCallback(() => {
    if (!containerRef.current) return

    const scrollableContainer = containerRef.current.closest(
      '.ai-segment-builder__content'
    ) as HTMLElement
    if (!scrollableContainer) return

    const messages = containerRef.current.querySelectorAll(
      '.thanx-ai-conversation__message'
    )
    if (messages.length === 0) return

    const lastMessage = messages[messages.length - 1] as HTMLElement
    const isUser = !lastMessage.classList.contains(
      'thanx-ai-conversation__message--ai'
    )
    const isTool = lastMessage.classList.contains(
      'thanx-ai-conversation__message--tool_call'
    )

    if (isUser && !isTool) {
      // Check if this is the first user message (skip scrolling for initial prompt)
      const userMessages = containerRef.current?.querySelectorAll(
        '.thanx-ai-conversation__message:not(.thanx-ai-conversation__message--ai):not(.thanx-ai-conversation__message--tool_call)'
      )
      const isFirstUserMessage = userMessages && userMessages.length === 1

      if (isFirstUserMessage) {
        updateScrollBuffer()
        return
      }

      const viewportHeight = scrollableContainer.clientHeight

      // Set temporary large height directly on scroll buffer
      if (scrollBufferRef.current) {
        scrollBufferRef.current.style.height = `${viewportHeight}px`
      }

      // Scroll to position
      const containerRect = scrollableContainer.getBoundingClientRect()
      const msgRect = lastMessage.getBoundingClientRect()
      const currentScrollTop = scrollableContainer.scrollTop

      const absoluteMsgPosition =
        currentScrollTop + (msgRect.top - containerRect.top)
      const targetOffsetTop = Math.max(
        0,
        absoluteMsgPosition - SCROLL_OFFSET_FROM_TOP
      )

      scrollableContainer.scrollTo({
        top: targetOffsetTop,
        behavior: 'smooth',
      })

      updateScrollBuffer()
    } else {
      // Recalculate for non-user messages
      updateScrollBuffer()
    }
  }, [updateScrollBuffer])

  scrollToNewMessageRef.current = handleNewMessage

  // External scroll trigger
  useEffect(() => {
    scrollToNewMessageRef.current?.()
  }, [scrollTrigger])

  // MutationObserver for new messages
  useEffect(() => {
    if (!enabled || !containerRef.current) return

    observerRef.current = new MutationObserver(mutations => {
      let shouldScroll = false

      mutations.forEach(m => {
        if (m.type === 'childList' && m.addedNodes.length > 0) {
          const addedMessages = Array.from(m.addedNodes).filter(
            node =>
              node instanceof Element &&
              node.classList.contains('thanx-ai-conversation__message')
          )
          if (addedMessages.length > 0) shouldScroll = true
        }
      })

      if (shouldScroll) {
        scrollToNewMessageRef.current?.()
      }
    })

    observerRef.current.observe(containerRef.current, {
      childList: true,
      subtree: true,
    })

    return () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    }
  }, [enabled])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ paddingTop: `${CONTAINER_PADDING_TOP}px` }}
    >
      {children}
      <ScrollBuffer
        ref={scrollBufferRef}
        containerRef={containerRef}
        scrollableSelector=".ai-segment-builder__content"
        trigger={scrollTrigger}
      />
    </div>
  )
}

export default ThanxAIAutoScrollContainer
